import { BaseEntityDto } from '../common';
import { OrganizationDto } from '../organization';

export interface ChallengeDto extends BaseEntityDto {
  organizationId: string;
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
  closeDate: string;
  status: ChallengeStatus;
  type: ChallengeType;
  programId?: string;
  inspectedOrg: OrganizationDto;
  inspectorId?: string;
}

export enum ChallengeStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export enum ChallengeType {
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL',
  CERTIFICATION = 'CERTIFICATION',
  VERIFICATION = 'VERIFICATION'
}

export interface CreateChallengeDto {
  name: string;
  description: string;
  inspectorId?: string;
  inspectedOrgIds: string[];
  programsVersionIds: string[];
  goalId?: string;
}

// Detailed Challenges Table API Response Types
export interface ChallengeTableInspectionsProgramsVersionsItem {
  id: string;
  programVersionItemId: string;
  inspectionProgramId: string;
  programVersionId: string;
  inspectionId: string;
  comments: string;
  isApproved: boolean;
  value: string;
  isActive: boolean;
  /**
   * ISO8601 UTC string, e.g. '2025-05-11T02:28:34.613954Z'. Can be null.
   */
  createdAt?: string | null;
  updatedAt?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

export interface ChallengeTableChallenge {
  inspectedOrgId: string;
  inspectedOrgName: string;
  inspectedOrgLogo: string;
  inspectedUserUuid: string;
  inspectedUserId: string;
  inspectedUserIdNumber: string;
  inspectedUserName: string;
  inspectedUserPhones: string[];
  inspectedOrgGroupsName: string[];
  inspectedOrgRegionName: string;
  inspectedOrgCityName: string;
  inspectedOrgVeredaName: string;
  inspectionsProgramsVersionsItems: ChallengeTableInspectionsProgramsVersionsItem[];
}

export interface ChallengeTableProgramVersionItem {
  id: string;
  description: string;
  name: string;
  versionId: string;
  programLevelId: string;
  programLevelName: string;
  isActive: boolean;
  measurementTypeId: string;
  measurementTemplate: string;  // Deprecated but still present
  formId?: string;               // References the Form entity (new forms system)
  cpNumber: string;
}

export interface ChallengeTableResponse {
  data: {
    challenges: ChallengeTableChallenge[];
    programVersionItems: ChallengeTableProgramVersionItem[];
  };
  message: string;
  type: string;
}

/**
 * Parameters for paginated challenges table endpoint
 */
export interface ChallengesTableParams {
  page?: number;
  size?: number;
  searchTerm?: string;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
}

/**
 * Paginated response for challenges table
 */
export interface PaginatedChallengeTableResponse {
  data: {
    challenges: ChallengeTableChallenge[];
    programVersionItems: ChallengeTableProgramVersionItem[];
  };
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
  message: string;
  type: string;
}

/**
 * Response for single organization challenge data (for FarmDetailsModal)
 */
export interface ChallengesByOrganizationResponse {
  data: {
    challenges: ChallengeTableChallenge[];
    programVersionItems: ChallengeTableProgramVersionItem[];
  };
  message: string;
  type: string;
}

/**
 * Result of syncing program version items to a challenge
 */
export interface SyncProgramVersionItemsResult {
  created: number;
  skipped: number;
  errors: string[];
}

/**
 * Result of syncing a single farm during batch sync
 */
export interface FarmSyncResult {
  farmId: string;
  farmName: string;
  challengesUpdated: number;
  created: number;
  skipped: number;
}

/**
 * Result of batch syncing program version items across multiple organizations
 */
export interface BatchSyncResult {
  totalCreated: number;
  totalSkipped: number;
  errors: string[];
  farmResults: FarmSyncResult[];
}

/**
 * Request DTO for adding organizations to an existing challenge
 */
export interface AddOrgsToChallengeDto {
  inspectedOrgIds: string[];
}

/**
 * Result DTO for adding organizations to an existing challenge
 */
export interface AddOrgsResultDto {
  created: number;
  skipped: number;
  skippedOrgNames: string[];
}

// ==================== Challenge Merge Types ====================

export enum ConflictResolution {
  AUTO_KEEP_SOURCE = 'AUTO_KEEP_SOURCE',
  AUTO_KEEP_TARGET = 'AUTO_KEEP_TARGET',
  MANUAL = 'MANUAL',
}

export interface MergePreviewRequestDto {
  targetChallengeId: string;
  sourceChallengeIds: string[];
}

export interface MergeConflictDto {
  inspectedOrgId: string;
  inspectedOrgName: string;
  sourceInspectionId: string;
  sourceAnsweredItems: number;
  targetInspectionId: string;
  targetAnsweredItems: number;
  resolution: ConflictResolution;
}

export interface MergePreviewResponseDto {
  targetChallengeName: string;
  sourceChallengeNames: string[];
  conflicts: MergeConflictDto[];
  farmsToRename: number;
}

export interface MergeResolutionDto {
  inspectedOrgId: string;
  keepInspectionId: string;
}

export interface MergeRequestDto {
  targetChallengeId: string;
  sourceChallengeIds: string[];
  resolutions: MergeResolutionDto[];
}

export interface MergeResultDto {
  renamed: number;
  softDeleted: number;
  skipped: number;
  errors: string[];
}

/**
 * Lightweight challenge template summary (grouped by name by the backend).
 */
export interface ChallengeTemplateDto {
  challengeId: string;
  name: string;
  orgCount: number;
}